/**
 * Error Boundary
 * ──────────────
 * Safety Bible §6: Error boundaries prevent white-screen crashes.
 * Wraps the app and individual screens to catch render errors.
 * Shows a recovery UI instead of a blank page.
 */
import React from 'react';
import { colors, font } from '@/design-system/tokens';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  screenName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Log to console (Sentry integration point for Sprint 6)
    console.error(`[ErrorBoundary${this.props.screenName ? `: ${this.props.screenName}` : ''}]`, error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div style={{
          background: colors.bgPrimary, minHeight: '100vh',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '0 32px', fontFamily: font.sans, color: colors.textPrimary, textAlign: 'center',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16,
            background: `${colors.red}12`, border: `1px solid ${colors.red}25`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colors.red} strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 400, margin: '0 0 8px' }}>Something went wrong</h2>
          <p style={{ fontSize: 13, color: colors.textSecondary, fontWeight: 300, margin: '0 0 24px', lineHeight: 1.6 }}>
            {this.props.screenName
              ? `The ${this.props.screenName} screen encountered an error.`
              : 'An unexpected error occurred.'}
          </p>

          {/* Error details (dev only) */}
          {this.state.error && (
            <div style={{
              padding: '12px 16px', background: colors.bgCard, border: `1px solid ${colors.border}`,
              borderRadius: 10, marginBottom: 20, textAlign: 'left', maxWidth: 400, width: '100%',
              maxHeight: 120, overflow: 'auto',
            }}>
              <div style={{ fontSize: 10, color: colors.red, fontFamily: font.mono, marginBottom: 4 }}>
                {this.state.error.name}: {this.state.error.message}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => this.setState({ hasError: false, error: null })}
              style={{
                padding: '12px 24px', background: colors.accent, borderRadius: 10,
                fontSize: 14, fontWeight: 600, color: colors.bgPrimary, cursor: 'pointer',
                border: 'none', fontFamily: font.sans,
              }}
            >
              Try again
            </button>
            <button
              onClick={() => { window.location.href = '/'; }}
              style={{
                padding: '12px 24px', background: colors.bgCard, border: `1px solid ${colors.border}`,
                borderRadius: 10, fontSize: 14, fontWeight: 500, color: colors.textSecondary,
                cursor: 'pointer', fontFamily: font.sans,
              }}
            >
              Go home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
